import {
  Box,
  Button,
  Chip,
  Dialog,
  FormControl,
  Grid2 as Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";

import { dbDate } from "@caredna-marlin/utils/dates";
import { usePredictContext } from "~/hooks/predict/usePredictContext";
import { useDischargeContext } from "~/hooks/discharge/useDischargeContext";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import { PredictProgram } from "~/types/predict";
import { resolveIntakeServiceLine } from "~/utils/intakeServiceLine";
import { getSelectedProviderName } from "~/components/discharge/patient-detail/buildPlaceMatches";
import { CREATE_PATIENT } from "~/components/patient/EditPatient";
import { GET_CLINICAL_CONSTRAINT_TEMPLATES_FOR_EPISODE_QUERY } from "~/queries/clinicalConstraintTemplate";
import {
  PatientSexEnum,
  GetClinicalConstraintTemplatesForEpisodeQuery,
  GetClinicalConstraintTemplatesForEpisodeQueryVariables,
} from "~/graphql/__generated__/graphql";
import { useSnackbar } from "~/hooks/useSnackbar";
import { pickDefaultTemplate } from "~/utils/clinicalTemplates";
import { EquipmentEta, makeEquipmentEta, formatEquipmentEta } from "~/utils/equipmentEta";
import EquipmentEtaRefreshButton from "~/components/common/EquipmentEtaRefreshButton";
import { CareReadinessDashboard } from "~/components/intake/CareReadinessDashboard";
import { formatPhoneNumber } from "~/utils/format";

type SortKey = "name" | "primaryDiagnosis" | "city" | "phone" | "program";
type SortDirection = "asc" | "desc";

const cols: { label: string; key: SortKey | null; sx?: SxProps<Theme> }[] = [
  { label: "Patient", key: "name" },
  { label: "Program", key: "program" },
  { label: "Home Address", key: "city" },
  { label: "Phone", key: "phone" },
  { label: "Primary Diagnosis", key: "primaryDiagnosis" },
  { label: "Equipment ETA", key: null },
  { label: "Template", key: null },
  { label: "", key: null, sx: { width: "1%", whiteSpace: "nowrap", py: 0, pr: 1, pl: 0 } },
];

// A single row in the merged Intake queue, sourced from either the Predict demo
// (Hospital at Home) or the Coordinate/Discharge demo (a provider that opts in
// to the intake treatment and resolves to a configured service line).
type IntakeQueueItem = {
  key: string;
  source: "predict" | "coordinate";
  firstName: string;
  lastName: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  address: string;
  phone: string | null;
  gender: string;
  dateOfBirth: string;
  mrn: string;
  age: number;
  primaryDiagnosis: string;
  // The service line this patient should be scheduled into. Predict/HaH leaves
  // this undefined and lets PatientBrief resolve it by name; Coordinate resolves
  // it explicitly from the selected provider.
  serviceLineId: number | undefined;
  programLabel: string;
  // Whether the target service line uses the autoscheduler. When false, the
  // clinical template is irrelevant (no care-requirements dialog), so the queue
  // shows "n/a" instead of a template picker.
  usesAutoscheduler: boolean;
  seedTemplateId?: number;
  seedTemplateName?: string;
  markScheduled: () => void;
};

const genderToSex = (gender: string): PatientSexEnum => {
  const g = gender.toLowerCase();
  if (g === "male") return PatientSexEnum.Male;
  if (g === "female") return PatientSexEnum.Female;
  return PatientSexEnum.Unknown;
};

const parseStreet = (address: string): string => {
  const parts = address.split(",").map((p) => p.trim());
  return parts[0] ?? address;
};

// Patient addresses in the demo are stored as a single string like
// "1234 Broadway, Nashville, TN 37203". Parse the tail into city/state/zip.
const parseAddress = (address: string): { city: string; state: string; zip: string } => {
  const parts = address.split(",").map((p) => p.trim());
  const tail = parts[parts.length - 1] ?? "";
  const cityCandidate = parts.length >= 2 ? parts[parts.length - 2] : "";
  const match = tail.match(/^([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);
  if (match) {
    return { city: cityCandidate, state: match[1], zip: match[2] };
  }
  return { city: cityCandidate, state: "", zip: tail };
};

// Split a "First Last" demo name into first/last for patient creation.
const splitName = (name: string): { firstName: string; lastName: string } => {
  const parts = name.trim().split(/\s+/);
  if (parts.length <= 1) return { firstName: name.trim(), lastName: "" };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
};

const sortValue = (item: IntakeQueueItem, key: SortKey): string => {
  switch (key) {
    case "name":
      return `${item.lastName}, ${item.firstName}`.toLowerCase();
    case "primaryDiagnosis":
      return item.primaryDiagnosis.toLowerCase();
    case "city":
      return item.city.toLowerCase();
    case "phone":
      return item.phone ?? "";
    case "program":
      return item.programLabel.toLowerCase();
  }
};

const PatientIntakeList = () => {
  const { patients: predictPatients, markPatientScheduled } = usePredictContext();
  const { patients: dischargePatients, markScheduled: markDischargeScheduled } = useDischargeContext();
  const navigate = useNavigate();
  const { setErrorMessage } = useSnackbar();
  const orgConfig = useOrgConfig();

  // Templates (same source as the Predict capacity check) so the Template column
  // can fall back to the template that would be auto-selected.
  const serviceLineId = Object.values(orgConfig.serviceLines).find(Boolean)?.id;
  const { data: templatesData } = useQuery<
    GetClinicalConstraintTemplatesForEpisodeQuery,
    GetClinicalConstraintTemplatesForEpisodeQueryVariables
  >(GET_CLINICAL_CONSTRAINT_TEMPLATES_FOR_EPISODE_QUERY, {
    variables: { serviceLineId: serviceLineId ?? 0 },
    skip: serviceLineId === undefined,
    fetchPolicy: "cache-and-network",
  });
  const templates = templatesData?.clinicalConstraintTemplate ?? [];
  const [createPatient, { loading: creatingPatient }] = useMutation(CREATE_PATIENT, {
    awaitRefetchQueries: true,
    refetchQueries: "active",
  });
  const [schedulingKey, setSchedulingKey] = useState<string | null>(null);
  const [readinessItem, setReadinessItem] = useState<IntakeQueueItem | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [etas, setEtas] = useState<Record<string, EquipmentEta>>({});
  const [templateOverrides, setTemplateOverrides] = useState<Record<string, number>>({});

  const intakeItems = useMemo<IntakeQueueItem[]>(() => {
    const predictItems: IntakeQueueItem[] = predictPatients
      .filter((p) => p.selectedProgram === PredictProgram.HOSPITAL_AT_HOME && !p.scheduledAt)
      .map((p) => {
        const { city, state, zip } = parseAddress(p.address);
        return {
          key: `predict:${p.id}`,
          source: "predict",
          firstName: p.firstName,
          lastName: p.lastName,
          street: parseStreet(p.address),
          city,
          state,
          zip,
          address: p.address,
          phone: p.phone ?? null,
          gender: p.gender,
          dateOfBirth: p.dateOfBirth,
          mrn: p.mrn,
          age: p.age,
          primaryDiagnosis: p.primaryDiagnosis,
          serviceLineId: undefined, // resolved by name (matchesHcah) in PatientBrief
          programLabel: "Hospital at Home",
          usesAutoscheduler: true, // HaH intake always runs the autoscheduler flow
          seedTemplateId: p.capacityCheck?.templateId,
          seedTemplateName: p.capacityCheck?.templateName,
          markScheduled: () => markPatientScheduled(p.id),
        };
      });

    const coordinateItems: IntakeQueueItem[] = dischargePatients
      .filter((p) => p.sentToIntakeAt && !p.scheduledAt)
      .map((p) => ({ p, sl: resolveIntakeServiceLine(getSelectedProviderName(p) ?? undefined, orgConfig.serviceLines) }))
      .filter(({ sl }) => sl !== undefined)
      .map(({ p, sl }) => {
        const address = p.homeAddress ?? "";
        const { city, state, zip } = parseAddress(address);
        const { firstName, lastName } = splitName(p.name);
        return {
          key: `coordinate:${p.id}`,
          source: "coordinate" as const,
          firstName,
          lastName,
          street: parseStreet(address),
          city,
          state,
          zip,
          address,
          phone: p.primaryContact.phone ?? null,
          gender: p.gender ?? "",
          dateOfBirth: p.dob,
          mrn: p.mrn,
          age: p.age,
          primaryDiagnosis: p.primaryDiagnosis,
          serviceLineId: sl!.id,
          programLabel: sl!.name,
          usesAutoscheduler: !!sl!.schedulingConfig?.useAutoscheduler,
          markScheduled: () => markDischargeScheduled(p.id),
        };
      });

    return [...predictItems, ...coordinateItems];
  }, [predictPatients, dischargePatients, orgConfig.serviceLines, markPatientScheduled, markDischargeScheduled]);

  // Seed an Equipment ETA for any patient that enters the queue without one.
  useEffect(() => {
    setEtas((prev) => {
      let changed = false;
      const next = { ...prev };
      const now = new Date();
      for (const item of intakeItems) {
        if (!next[item.key]) {
          next[item.key] = makeEquipmentEta(now);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [intakeItems]);

  const handleEtaRefreshed = (key: string, etaMs: number) => {
    setEtas((prev) => (prev[key] ? { ...prev, [key]: { ...prev[key], etaMs } } : prev));
  };

  const handleSchedule = async (item: IntakeQueueItem) => {
    setSchedulingKey(item.key);
    try {
      const result = await createPatient({
        variables: {
          firstName: item.firstName,
          lastName: item.lastName,
          phone: item.phone ?? null,
          addressStreet: item.street,
          addressLineTwo: null,
          addressCity: item.city,
          addressState: item.state,
          addressZip: item.zip,
          sex: genderToSex(item.gender),
          dateOfBirth: item.dateOfBirth ? dbDate(new Date(item.dateOfBirth)) : null,
          patientNotes: "",
          serviceInstructions: "",
          patientIdentifiers: item.mrn ? [{ identifier: item.mrn, identifier_type: "MRN" }] : [],
          addressPlaceId: null,
        },
      });
      const created = result.data?.createPatient;
      if (created?.id) {
        item.markScheduled();
        // Carry the selected/recommended template so the new episode's template
        // picker defaults to it.
        const selectedTemplateId =
          templateOverrides[item.key] ?? item.seedTemplateId ?? pickDefaultTemplate(templates, item.key)?.id;
        const templateName = templates.find((t) => t.id === selectedTemplateId)?.name ?? item.seedTemplateName;
        const equipmentEtaMs = etas[item.key]?.etaMs;
        navigate(`/patients/${created.id}`, {
          state: { startNewEpisode: "hcah", serviceLineId: item.serviceLineId, templateName, equipmentEtaMs },
        });
      }
    } catch (e) {
      console.error(e);
      setErrorMessage("Failed to create patient from intake queue");
    } finally {
      setSchedulingKey(null);
    }
  };

  const sortedItems = useMemo(() => {
    const copy = [...intakeItems];
    copy.sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      if (av === bv) return 0;
      const cmp = av < bv ? -1 : 1;
      return sortDirection === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [intakeItems, sortKey, sortDirection]);

  const visibleItems = useMemo(
    () => sortedItems.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [sortedItems, page, rowsPerPage],
  );

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  return (
    <Box sx={{ height: "100%", width: "100%", overflow: "hidden", mt: 4, mb: 4 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ width: "100%", p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="h6">Intake Queue</Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {cols.map(({ label, key, sx }) => (
                      <TableCell key={label || "actions"} onClick={() => key && handleSort(key)} sx={sx}>
                        {key ? (
                          <TableSortLabel
                            active={sortKey === key}
                            direction={sortKey === key ? sortDirection : "asc"}
                          >
                            {label}
                          </TableSortLabel>
                        ) : (
                          label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={cols.length} sx={{ textAlign: "center" }}>
                        No patients in intake queue
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleItems.map((item) => {
                      const eta = etas[item.key];
                      const recommendedTemplateId = pickDefaultTemplate(templates, item.key)?.id;
                      const selectedTemplateId: number | "" =
                        templateOverrides[item.key] ?? item.seedTemplateId ?? recommendedTemplateId ?? "";
                      return (
                        <TableRow key={item.key}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
                              {item.firstName} {item.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                              {item.mrn} • {item.age} yrs
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={item.programLabel}
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
                              {item.street || "—"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                              {[item.city, [item.state, item.zip].filter(Boolean).join(" ")]
                                .filter(Boolean)
                                .join(", ")}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatPhoneNumber(item.phone, "—")}</TableCell>
                          <TableCell>{item.primaryDiagnosis}</TableCell>
                          <TableCell>
                            {eta ? (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Typography variant="body2">{formatEquipmentEta(eta)}</Typography>
                                <EquipmentEtaRefreshButton
                                  etaMs={eta.etaMs}
                                  onRefreshed={(newMs) => handleEtaRefreshed(item.key, newMs)}
                                />
                              </Box>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell>
                            {item.usesAutoscheduler ? (
                              <FormControl size="small" sx={{ minWidth: 150 }}>
                                <Select
                                  value={templates.some((t) => t.id === selectedTemplateId) ? selectedTemplateId : ""}
                                  onChange={(e) =>
                                    setTemplateOverrides((prev) => ({ ...prev, [item.key]: Number(e.target.value) }))
                                  }
                                  displayEmpty
                                  renderValue={(val) => {
                                    const t = templates.find((tt) => tt.id === val);
                                    if (!t) {
                                      return <Box component="span" sx={{ color: "text.disabled" }}>Select template</Box>;
                                    }
                                    return val === recommendedTemplateId ? `${t.name} *` : t.name;
                                  }}
                                >
                                  {templates.map((t) => (
                                    <MenuItem key={t.id} value={t.id}>
                                      {t.name}
                                      {t.id === recommendedTemplateId && (
                                        <Tooltip title="Selected based on recent activity in patient's chart">
                                          <Box component="span" sx={{ ml: 0.5, cursor: "help" }}>*</Box>
                                        </Tooltip>
                                      )}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : (
                              <Typography variant="body2" color="text.secondary">n/a</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ width: "1%", whiteSpace: "nowrap", py: 0, pr: 1, pl: 0 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "flex-end" }}>
                              {item.usesAutoscheduler && (
                                <Tooltip title="Care Readiness Dashboard">
                                  <IconButton
                                    color="primary"
                                    onClick={() => setReadinessItem(item)}
                                  >
                                    <MonitorHeartIcon sx={{ fontSize: 28 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleSchedule(item)}
                                disabled={creatingPatient}
                                loading={creatingPatient && schedulingKey === item.key}
                              >
                                Schedule Visits
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={sortedItems.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setPage(0);
                setRowsPerPage(parseInt(e.target.value));
              }}
            />
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={readinessItem !== null}
        onClose={() => setReadinessItem(null)}
        maxWidth="md"
        fullWidth
      >
        {readinessItem && (
          <Box sx={{ position: "relative" }}>
            <IconButton
              onClick={() => setReadinessItem(null)}
              size="small"
              sx={{ position: "absolute", top: 8, right: 8, color: "common.white", zIndex: 1 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <CareReadinessDashboard
              patientName={`${readinessItem.firstName.charAt(0)}. ${readinessItem.lastName}`}
              programLabel={readinessItem.programLabel}
            />
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default PatientIntakeList;
